import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

import { confirmar } from "@/components/ConfirmDialog";
const PROFILES = [
  { v: "professor", l: "Professor(a)" },
  { v: "instrutor", l: "Instrutor(a)" },
  { v: "mentor", l: "Mentor(a)" },
  { v: "representante", l: "Representante Credenciado" },
];

/** Admin: editar dados e definir nova senha de um aluno ou professor. kind = "student" | "instructor" */
export const EditPersonDialog = ({ person, kind, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", phone: "", profile: "professor", password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [nomeLiberado, setNomeLiberado] = useState(false);
  const [emailLiberado, setEmailLiberado] = useState(false);
  const [emailNovo, setEmailNovo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    if (person) { setForm({ name: person.name || "", phone: person.phone || "", profile: person.profile || "professor", password: "", confirm: "" }); setNomeLiberado(false); setEmailLiberado(false); setEmailNovo(person.email || ""); }
  }, [person]);

  const registered = person?.status === "cadastrado";

  const save = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome.");
    if (form.password && form.password !== form.confirm) return toast.error("A confirmação não confere com a nova senha.");
    setSaving(true);
    try {
      const body = { name: form.name, phone: form.phone, password: form.password || null };
      if (kind === "instructor") body.profile = form.profile;
      const r = await api.put(`/${kind === "instructor" ? "instructors" : "students"}/${person.id}`, body);
      toast.success(form.password ? "Dados e senha atualizados!" : "Dados atualizados!");
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!person} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Editar {kind === "instructor" ? "professor(a)" : "aluno(a)"}</DialogTitle>
          <DialogDescription>{person?.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Nome completo *</Label>
            <div className="flex gap-2">
              <Input value={form.name} readOnly={!nomeLiberado} className={nomeLiberado ? "" : "bg-slate-50"} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} data-testid="edit-person-name" />
              {!nomeLiberado && <Button type="button" variant="outline" onClick={async () => (await confirmar("O nome consta no contrato, nos certificados e nas notas fiscais. Alterar mesmo assim?")) && setNomeLiberado(true)} data-testid="edit-person-name-unlock">Alterar</Button>}
            </div>
            <p className="text-[11px] text-slate-400">O nome consta no contrato, nos certificados e nas notas fiscais. Bloqueado para evitar alterações acidentais: só a escola altera, pelo botão ao lado.</p>
          </div>
          <div className="space-y-1.5">
            <Label>E-mail</Label>
            <div className="flex gap-2">
              <Input value={emailLiberado ? emailNovo : (person?.email || "")} readOnly={!emailLiberado} type="email" className={emailLiberado ? "" : "bg-slate-50"} onChange={(e) => setEmailNovo(e.target.value)} data-testid="edit-person-email" />
              {!emailLiberado ? <Button type="button" variant="outline" onClick={async () => (await confirmar("O e-mail é o login da pessoa e recebe o contrato e a nota fiscal. Alterar mesmo assim?")) && (setEmailNovo(person?.email || ""), setEmailLiberado(true))} data-testid="edit-person-email-unlock">Alterar</Button> : (
                <Button type="button" disabled={emailBusy} onClick={async () => { setEmailBusy(true); try { const r = await api.put(`/admin/people/${kind === "instructor" ? "professor" : "aluno"}/${person.id}/email`, { email: emailNovo }); toast.success("E-mail alterado."); onSaved?.({ ...person, email: r.data.email }); onClose(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setEmailBusy(false); } }} className="text-white" style={{ background: "#1E9E2A" }}>{emailBusy ? "Salvando…" : "Salvar e-mail"}</Button>)}
            </div>
            <p className="text-[11px] text-slate-400">É o e-mail de acesso e recebe o contrato e a nota fiscal. Bloqueado para evitar alterações acidentais: só a escola altera, pelo botão ao lado.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Telefone</Label>
            <Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="(00) 00000-0000" />
          </div>
          {kind === "instructor" && (
            <div className="space-y-1.5">
              <Label>Perfil</Label>
              <Select value={form.profile} onValueChange={(v) => setForm((p) => ({ ...p, profile: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROFILES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center gap-2 font-semibold text-slate-800 text-sm">
              <KeyRound className="h-4 w-4 text-[#1E9E2A]" /> {registered ? "Atualizar senha" : "Definir senha de acesso"}
            </div>
            <p className="text-xs text-slate-500">
              {registered
                ? "Deixe em branco para manter a senha atual."
                : "Ainda não concluiu o cadastro. Ao definir uma senha, o acesso é liberado na hora, sem precisar do convite."}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input type="password" autoComplete="new-password" placeholder="Nova senha" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} data-testid="edit-person-password" />
              <Input type="password" autoComplete="new-password" placeholder="Confirmar" value={form.confirm} onChange={(e) => setForm((p) => ({ ...p, confirm: e.target.value }))} />
            </div>
            <p className="text-[11px] text-slate-400">Mínimo de 8 caracteres.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#232D4F" }} data-testid="edit-person-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
